/**
 * Boot-time FileSource resolution for the viewer surface — the workbench
 * counterpart of `useSources` + `resolveShareBoot`, so a store-driven
 * viewer acquires its workspace through the same precedence the legacy
 * App did:
 *
 *   viewer:  `#s=` share link  >  `?src=` remote  >  persisted upload  >  demo
 *   preview: the backing server (`/api/files` + `/api/events` SSE)
 *
 * Pure precedence logic over `window.location` pieces passed in by the
 * caller — no DOM access here, so boot wiring stays unit-testable. Beside
 * the source itself the result carries what a share link encodes beyond
 * files: the view to restore (seed `createWorkspaceStore` with it) and the
 * optional compare side of a shared diff.
 */
import type { Vfs } from "@archlang/viewer-core";
import { DEFAULT_VFS, DEFAULT_ASSETS } from "../defaultSource.js";
import { fetchRemoteWorkspace, parseSrcUrl } from "../remoteSource.js";
import { resolveShareBoot } from "../viewerBoot.js";
import { loadPersistedSources } from "../viewerSources.js";
import type { PersistedViewerState } from "../viewerStorage.js";
import {
  READONLY,
  type FileSource,
  type WorkspaceSnapshot,
} from "../workbench/filesource/types.js";
import { createSnapshotFileSource } from "../workbench/filesource/snapshot.js";
import {
  createServerFileSource,
  type ServerFileSourceOptions,
} from "../workbench/filesource/server.js";
import { createUploadedFileSource } from "../workbench/filesource/uploaded.js";
import type { SourceMode } from "./useSources.js";

export type ViewerSourceOrigin =
  | "shared"
  | "remote"
  | "uploaded"
  | "demo"
  | "server";

export const DEMO_LABEL = "demo (examples)";

/**
 * Content-hashed overlay key for the bundled demo. The demo overlay snapshots
 * the WHOLE demo VFS on first edit and then overrides the bundled `DEFAULT_VFS`
 * on every load (see `createLocalFileSource`). A static key (`…:demo:v1`) meant
 * a stale overlay kept shadowing the bundle forever after the demo content
 * changed — rebuilds had no effect until the user cleared localStorage. Keying
 * by a hash of `DEFAULT_VFS` makes any demo change mint a fresh key, so the
 * stale overlay is abandoned automatically while edits to a given demo version
 * still persist across reloads.
 */
export function hashVfs(vfs: Vfs): string {
  // FNV-1a over sorted "path\0content" — order-independent, deterministic.
  let h = 0x811c9dc5;
  const entries = [...vfs.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  for (const [path, content] of entries) {
    const s = `${path}\0${content}\0`;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
  }
  return (h >>> 0).toString(36);
}

const DEMO_OVERLAY_KEY = `archlang:viewer:overlay:demo:${hashVfs(DEFAULT_VFS)}`;

export interface ViewerBootSource {
  readonly source: FileSource;
  readonly origin: ViewerSourceOrigin;
  /** localStorage key for the working-edit overlay. Set for the demo + `?src=`
   *  remote so that toggling the editor on and editing persists across reload
   *  (the "scratch" the old editable viewer had — now the one viewer keeps it).
   *  Omitted for share/upload/server, which carry their own persistence model. */
  readonly overlayKey?: string;
  /** Viewport + selection carried by a share link, or null. Map onto the
   *  store's `initialSelection` / `initialViewport` seeds. */
  readonly viewerState: PersistedViewerState | null;
  /** The other side of a shared diff, or null. */
  readonly compare: {
    readonly vfs: Vfs;
    readonly label: string;
    readonly swapped: boolean;
  } | null;
}

export interface ViewerBootInput {
  readonly mode: SourceMode;
  /** `window.location.hash` — checked for a `#s=` share link. */
  readonly hash?: string;
  /** `window.location.search` — checked for a `?src=` remote URL. */
  readonly search?: string;
  /** Forwarded to the server source (preview mode). */
  readonly server?: Pick<
    ServerFileSourceOptions,
    "filesUrl" | "eventsUrl" | "label" | "onWidgetsChanged" | "eventStream"
  >;
  /** Injectable for tests; defaults to global `fetch`. */
  readonly fetchImpl?: typeof fetch;
}

export function resolveViewerFileSource(
  input: ViewerBootInput,
): ViewerBootSource {
  if (input.mode === "preview") {
    return {
      source: createServerFileSource({
        ...input.server,
        fetchImpl: input.fetchImpl,
      }),
      origin: "server",
      viewerState: null,
      compare: null,
    };
  }

  // A share link always wins on first load — it's a self-contained
  // workspace plus the exact view its sender saw.
  const share = resolveShareBoot(input.hash ?? "");
  if (share.sources) {
    return {
      source: createSnapshotFileSource({
        label: share.sources.label,
        files: share.sources.vfs,
        assets: share.sources.assets,
      }),
      origin: "shared",
      viewerState: share.viewerState,
      compare: share.compare,
    };
  }

  const srcUrl = parseSrcUrl(input.search ?? "");
  if (srcUrl) {
    return {
      source: createRemoteBootSource(srcUrl, input.fetchImpl),
      origin: "remote",
      // Experiments on a readonly repo persist locally, keyed per URL.
      overlayKey: `archlang:viewer:overlay:${srcUrl}`,
      viewerState: null,
      compare: null,
    };
  }

  const uploaded = loadPersistedSources();
  if (uploaded) {
    return {
      source: createUploadedFileSource(uploaded),
      origin: "uploaded",
      viewerState: null,
      compare: null,
    };
  }

  return {
    source: createSnapshotFileSource({ label: DEMO_LABEL, files: DEFAULT_VFS, assets: DEFAULT_ASSETS }),
    origin: "demo",
    // Editing the demo (via the editor toggle) persists locally — the viewer's
    // scratch. Keyed by a content hash of the bundled demo, so any change to the
    // demo abandons the prior overlay instead of letting it shadow the bundle.
    overlayKey: DEMO_OVERLAY_KEY,
    viewerState: null,
    compare: null,
  };
}

/**
 * `?src=` source with the legacy failure behaviour: a failed fetch keeps
 * the user's workspace (persisted upload, else demo) instead of an empty
 * board. The fallback rides readonly here — acceptable degradation for the
 * error path of an explicitly-followed link. Label resolves after the
 * fetch (getter), like the server source's root name.
 */
function createRemoteBootSource(
  url: string,
  fetchImpl?: typeof fetch,
): FileSource {
  let label = remoteLabel(url);
  let cached: Promise<WorkspaceSnapshot | null> | null = null;

  const pull = async (): Promise<WorkspaceSnapshot> => {
    const loaded = await fetchRemoteWorkspace(url, { fetch: fetchImpl });
    if (loaded) {
      label = loaded.label;
      return { files: loaded.vfs, activeFile: null, assets: loaded.assets };
    }
    const persisted = loadPersistedSources();
    if (persisted) {
      label = persisted.label;
      return { files: persisted.vfs, activeFile: null };
    }
    label = DEMO_LABEL;
    return { files: DEFAULT_VFS, activeFile: null, assets: DEFAULT_ASSETS };
  };

  return {
    get label() {
      return label;
    },
    capabilities: READONLY,
    // Memoized: a StrictMode double-mount must not fetch the URL twice.
    load: () => (cached ??= pull()),
  };
}

function remoteLabel(url: string): string {
  try {
    return (
      new URL(url).pathname.split("/").filter(Boolean).pop() ?? "remote"
    );
  } catch {
    return "remote";
  }
}
